import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Search, X, Loader2, Sparkles } from 'lucide-react';

/**
 * EspecializacoesAutocomplete — campo de tags controladas com autocomplete.
 *
 * Uso:
 * <EspecializacoesAutocomplete
 *   subcategoriaId="alimentos-bebidas__restaurantes"
 *   categoriaId="alimentos"
 *   valueControladas={form.tags_controladas_ids}
 *   valueLivres={form.tags_livres_pendentes}
 *   onChangeControladas={(ids) => setForm(...)}
 *   onChangeLivres={(livres) => setForm(...)}
 *   maxControladas={8}
 *   maxLivres={3}
 * />
 *
 * Regras:
 * - Sem logos/ícones de marca de terceiros — apenas texto puro em chip neutro.
 * - Debounce de 300ms na busca.
 * - Enter no input adiciona como "livre pendente" se não bater com nenhuma
 *   sugestão controlada.
 */
export default function EspecializacoesAutocomplete({
  subcategoriaId,
  categoriaId,
  valueControladas = [],
  valueLivres = [],
  onChangeControladas,
  onChangeLivres,
  maxControladas = 8,
  maxLivres = 3,
  disabled = false,
  api, // { get: (url, {params}) => Promise<{data}> } — injetado
  testIdPrefix = 'esp',
}) {
  const [termo, setTermo] = useState('');
  const [sugestoes, setSugestoes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [tagsResolvidas, setTagsResolvidas] = useState({}); // { id: {nome, tipo} }
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Resolver nomes das tags controladas selecionadas (para mostrar chips)
  useEffect(() => {
    if (!api || valueControladas.length === 0) return;
    const missing = valueControladas.filter((id) => !tagsResolvidas[id]);
    if (missing.length === 0) return;
    (async () => {
      try {
        // Se temos sub, busca as tags dela e cacheia
        const params = subcategoriaId ? { subcategoria_id: subcategoriaId } : {};
        const r = await api.get('/tags/subcategoria/' + (subcategoriaId || 'x'), { params });
        const items = (r?.data?.items) || [];
        const map = {};
        items.forEach((t) => { map[t.id] = t; });
        setTagsResolvidas((prev) => ({ ...prev, ...map }));
      } catch (_e) {
        // fallback: mostrar id como fallback
        const map = {};
        missing.forEach((id) => { map[id] = { id, nome: id, tipo: 'geral' }; });
        setTagsResolvidas((prev) => ({ ...prev, ...map }));
      }
    })();
  }, [valueControladas, subcategoriaId, api, tagsResolvidas]);

  // Busca com debounce (300ms)
  const buscar = useCallback(async (q) => {
    if (!api) return;
    setLoading(true);
    try {
      const params = { termo: q, limit: 10 };
      if (subcategoriaId) params.subcategoria_id = subcategoriaId;
      else if (categoriaId) params.categoria_id = categoriaId;
      const r = await api.get('/tags/buscar', { params });
      const items = (r?.data?.items) || [];
      // filtra já selecionadas
      const filtradas = items.filter((t) => !valueControladas.includes(t.id));
      setSugestoes(filtradas);
      // cache para exibição
      const map = {};
      items.forEach((t) => { map[t.id] = t; });
      setTagsResolvidas((prev) => ({ ...prev, ...map }));
    } catch (_e) {
      setSugestoes([]);
    } finally {
      setLoading(false);
    }
  }, [api, subcategoriaId, categoriaId, valueControladas]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!termo || termo.trim().length < 2) {
      setSugestoes([]);
      return;
    }
    debounceRef.current = setTimeout(() => buscar(termo.trim()), 300);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [termo, buscar]);

  const canAddMoreControladas = valueControladas.length < maxControladas;
  const canAddMoreLivres = valueLivres.length < maxLivres;

  const addControlada = (tag) => {
    if (!canAddMoreControladas || valueControladas.includes(tag.id)) return;
    onChangeControladas?.([...valueControladas, tag.id]);
    setTermo('');
    setSugestoes([]);
    setShowDropdown(false);
  };

  const removeControlada = (id) => {
    onChangeControladas?.(valueControladas.filter((x) => x !== id));
  };

  const addLivre = () => {
    const t = termo.trim();
    if (!t || !canAddMoreLivres) return;
    // não duplica
    if (valueLivres.some((x) => x.toLowerCase() === t.toLowerCase())) {
      setTermo('');
      return;
    }
    onChangeLivres?.([...valueLivres, t]);
    setTermo('');
    setSugestoes([]);
    setShowDropdown(false);
  };

  const removeLivre = (v) => {
    onChangeLivres?.(valueLivres.filter((x) => x !== v));
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Se há sugestão exata, adiciona controlada; senão vira livre
      const exact = sugestoes.find((t) => t.nome.toLowerCase() === termo.trim().toLowerCase());
      if (exact) addControlada(exact);
      else if (termo.trim().length >= 2) addLivre();
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  return (
    <div className="w-full" ref={wrapperRef} data-testid={`${testIdPrefix}-wrapper`}>
      <label className="block text-sm font-medium mb-2">
        Especializações
        <span className="text-xs text-gray-400 font-normal ml-2">
          (opcional — até {maxControladas} tags sugeridas + {maxLivres} extras)
        </span>
      </label>
      <p className="text-xs text-gray-500 mb-3">
        Digite pra ver sugestões. Exemplos: <em>comida japonesa</em>,
        <em> Honda</em>, <em>injeção eletrônica</em>. Ajuda seus clientes a
        encontrar exatamente o que você faz.
      </p>

      {/* Chips selecionados */}
      {(valueControladas.length > 0 || valueLivres.length > 0) && (
        <div className="flex flex-wrap gap-2 mb-3" data-testid={`${testIdPrefix}-chips`}>
          {valueControladas.map((id) => {
            const t = tagsResolvidas[id];
            const nome = t ? t.nome : id;
            return (
              <span
                key={id}
                className="inline-flex items-center gap-1 bg-[var(--primary)]/10 text-[var(--primary)] px-3 py-1 rounded-full text-sm font-medium border border-[var(--primary)]/20"
                data-testid={`${testIdPrefix}-chip-controlada-${id}`}
              >
                {nome}
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => removeControlada(id)}
                    className="ml-1 hover:bg-[var(--primary)]/20 rounded-full p-0.5"
                    aria-label={`Remover ${nome}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </span>
            );
          })}
          {valueLivres.map((v) => (
            <span
              key={v}
              className="inline-flex items-center gap-1 bg-amber-50 text-amber-800 px-3 py-1 rounded-full text-sm font-medium border border-amber-200"
              title="Aguardando curadoria"
              data-testid={`${testIdPrefix}-chip-livre-${v.replace(/\s+/g, '-')}`}
            >
              {v}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => removeLivre(v)}
                  className="ml-1 hover:bg-amber-100 rounded-full p-0.5"
                  aria-label={`Remover ${v}`}
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      {/* Input com autocomplete */}
      {!disabled && (canAddMoreControladas || canAddMoreLivres) && (
        <div className="relative">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={termo}
              onChange={(e) => { setTermo(e.target.value); setShowDropdown(true); }}
              onFocus={() => setShowDropdown(true)}
              onKeyDown={onKeyDown}
              placeholder={
                subcategoriaId
                  ? 'Comece a digitar para ver sugestões...'
                  : 'Escolha a subcategoria primeiro para ver sugestões'
              }
              disabled={!subcategoriaId && !categoriaId}
              className="w-full border border-gray-200 rounded-lg pl-10 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--primary)]/30 disabled:bg-gray-50 disabled:text-gray-400"
              data-testid={`${testIdPrefix}-input`}
            />
            {loading && (
              <Loader2 className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 animate-spin" />
            )}
          </div>

          {showDropdown && (sugestoes.length > 0 || (termo.trim().length >= 2 && !loading)) && (
            <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-30 max-h-72 overflow-y-auto" data-testid={`${testIdPrefix}-dropdown`}>
              {sugestoes.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => addControlada(t)}
                  className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 flex items-center justify-between gap-2"
                  data-testid={`${testIdPrefix}-sugestao-${t.id}`}
                >
                  <span className="text-sm">
                    <span className="font-medium">{t.nome}</span>
                    {t.tipo === 'marca' && (
                      <span className="ml-2 text-xs text-gray-400">marca</span>
                    )}
                    {t.tipo === 'especializacao' && (
                      <span className="ml-2 text-xs text-gray-400">especialização</span>
                    )}
                  </span>
                  {t.contagem_uso > 0 && (
                    <span className="text-xs text-gray-400">
                      {t.contagem_uso} usos
                    </span>
                  )}
                </button>
              ))}
              {sugestoes.length === 0 && termo.trim().length >= 2 && !loading && canAddMoreLivres && (
                <button
                  type="button"
                  onClick={addLivre}
                  className="w-full text-left px-3 py-2 hover:bg-amber-50 flex items-center gap-2 text-amber-800"
                  data-testid={`${testIdPrefix}-add-livre`}
                >
                  <Sparkles className="w-4 h-4" />
                  <span className="text-sm">
                    Adicionar &quot;<b>{termo.trim()}</b>&quot; como sugestão livre
                    (aguardará curadoria)
                  </span>
                </button>
              )}
              {sugestoes.length === 0 && termo.trim().length >= 2 && !loading && !canAddMoreLivres && (
                <div className="px-3 py-2 text-xs text-gray-500">
                  Limite de {maxLivres} tags livres atingido — remova alguma para
                  adicionar outra.
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Contador */}
      <div className="text-xs text-gray-400 mt-2 flex justify-between">
        <span>
          {valueControladas.length}/{maxControladas} sugeridas ·{' '}
          {valueLivres.length}/{maxLivres} livres
        </span>
        {valueLivres.length > 0 && (
          <span className="text-amber-600">
            {valueLivres.length} aguardando curadoria
          </span>
        )}
      </div>
    </div>
  );
}
